import { useCallback, useEffect, useState } from 'react';

// `beforeinstallprompt` carries a `prompt()` that triggers the native
// install dialog. Browsers fire it once per session on installable PWAs;
// our job is to capture it and surface a banner the user can opt into
// or dismiss.
type BeforeInstallPromptEvent = Event & {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
};

const SUPPRESSION_KEY = 'food-diary:install-prompt-suppressed-until';
const SUPPRESSION_MS = 7 * 24 * 60 * 60 * 1000;

function isSuppressed(now: number): boolean {
  if (typeof localStorage === 'undefined') return false;
  const raw = localStorage.getItem(SUPPRESSION_KEY);
  if (!raw) return false;
  const until = Number.parseInt(raw, 10);
  return Number.isFinite(until) && until > now;
}

function isStandalone(): boolean {
  // Chrome / Edge: `display-mode: standalone` media query
  // Safari: `navigator.standalone` (legacy)
  const mql = globalThis.matchMedia('(display-mode: standalone)');
  if (mql.matches) return true;
  return Boolean((navigator as unknown as { readonly standalone?: boolean }).standalone);
}

export type InstallPromptState =
  | { readonly status: 'unavailable' }
  | { readonly status: 'suppressed' }
  | { readonly status: 'installed' }
  | {
      readonly status: 'available';
      readonly promptInstall: () => Promise<'accepted' | 'dismissed'>;
      readonly dismiss: () => void;
    };

export function useInstallPrompt(now: () => number = () => Date.now()): InstallPromptState {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [suppressed, setSuppressed] = useState<boolean>(() => isSuppressed(now()));

  useEffect(() => {
    if (typeof globalThis === 'undefined') return;
    const handleBeforePrompt = (e: Event): void => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    const handleInstalled = (): void => {
      setInstalled(true);
      setEvent(null);
    };
    globalThis.addEventListener('beforeinstallprompt', handleBeforePrompt);
    globalThis.addEventListener('appinstalled', handleInstalled);
    return () => {
      globalThis.removeEventListener('beforeinstallprompt', handleBeforePrompt);
      globalThis.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed'> => {
    if (!event) return 'dismissed';
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === 'dismissed') {
      // Same as the explicit "later" — don't badger.
      localStorage.setItem(SUPPRESSION_KEY, String(now() + SUPPRESSION_MS));
      setSuppressed(true);
    }
    setEvent(null);
    return outcome;
  }, [event, now]);

  const dismiss = useCallback((): void => {
    localStorage.setItem(SUPPRESSION_KEY, String(now() + SUPPRESSION_MS));
    setSuppressed(true);
    setEvent(null);
  }, [now]);

  if (installed) return { status: 'installed' };
  if (suppressed) return { status: 'suppressed' };
  if (!event) return { status: 'unavailable' };
  return { status: 'available', promptInstall, dismiss };
}

export const __testing = { SUPPRESSION_KEY, SUPPRESSION_MS };
